import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Inbox, Pencil, Eye } from "lucide-react";
import { useAssignedToMe } from "@/hooks";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * The forms other people have asked the logged-in user to fill in or follow.
 * Backed by the additive assigned-to-me endpoint, so it never touches the
 * existing queue-access rules.
 */
const MyDelegationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useAssignedToMe();
  const delegations: any[] = data?.delegations ?? [];

  return (
    <div className="mx-auto w-full max-w-4xl p-4">
      <div className="mb-4 flex items-center gap-2">
        <Inbox className="h-5 w-5 text-darkBlue" />
        <h1 className="text-xl font-semibold text-gray-900">
          {t("delegation.mine.title")}
        </h1>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : delegations.length === 0 ? (
        <p className="py-12 text-center text-gray-500">
          {t("delegation.mine.empty")}
        </p>
      ) : (
        <ul className="space-y-3">
          {delegations.map((d) => {
            const isEditor = d.myRole === "EDITOR";
            return (
              <li
                key={d.id}
                className="rounded border border-gray-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-gray-900">
                      {d.form?.name ?? t("delegation.mine.aForm")}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t("delegation.mine.from", {
                        name: d.ownerName || d.ownerEmail,
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      {isEditor ? (
                        <>
                          <Pencil className="h-3.5 w-3.5" />
                          {t("delegation.owner.editorLabel")}
                        </>
                      ) : (
                        <>
                          <Eye className="h-3.5 w-3.5" />
                          {t("delegation.owner.viewerLabel")}
                        </>
                      )}
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${
                        d.status === "PENDING"
                          ? "bg-sky-100 text-sky-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {t(`delegation.mine.status.${d.status}`)}
                    </span>
                    <Link
                      to={`/delegated-to-me/${d.id}`}
                      className="main-dark-button text-xs"
                    >
                      {isEditor && d.status === "PENDING"
                        ? t("delegation.mine.open")
                        : t("delegation.mine.view")}
                    </Link>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  {t("delegation.owner.sentAt", {
                    time: dayjs(d.createdAt).format("MMM D, YYYY, h:mm A"),
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MyDelegationsPage;
